import { computed, onMounted, onScopeDispose, ref, watch } from 'vue';
import { captureAuthRequestSession, promptAuthLogin } from '@/utils/auth-session';
import { getDailyCheckInStatus } from '@/api/trade';
import { useAuth } from '@/composables/useAuth';

/** PC、H5 和弹窗共用同一份服务端签到状态。 */
const open = ref(false);
const status = ref(null);
const today = ref(new Date().toISOString().slice(0, 10));
const checkedInToday = computed(
  () => status.value?.today === today.value && status.value?.checkedInToday === true
);
let requestVersion = 0;
let pending = null;

function isCurrentSession(session) {
  const current = captureAuthRequestSession();
  return session.token === current.token && session.generation === current.generation;
}

function applyStatus(next, session = captureAuthRequestSession()) {
  if (!isCurrentSession(session)) return false;
  requestVersion += 1;
  pending = null;
  today.value = new Date().toISOString().slice(0, 10);
  status.value = next;
  return true;
}

function refreshStatus() {
  today.value = new Date().toISOString().slice(0, 10);
  if (pending) return pending;
  const session = captureAuthRequestSession();
  const version = ++requestVersion;
  pending = getDailyCheckInStatus()
    .then((next) => {
      if (version === requestVersion && isCurrentSession(session)) applyStatus(next, session);
    })
    .finally(() => {
      if (version === requestVersion) pending = null;
    });
  return pending;
}

/** 仅由 App 安装：登录、UTC 跨日及回到页面时重新读取状态。 */
export function useDailyCheckInStatusSync() {
  const { isLoggedIn } = useAuth();
  let timer;
  function refresh() {
    today.value = new Date().toISOString().slice(0, 10);
    if (isLoggedIn.value) {
      void refreshStatus().catch((err) => console.warn('[daily-check-in] status load failed', err));
    }
  }
  function scheduleNextDay() {
    clearTimeout(timer);
    const now = new Date();
    const nextDay = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1);
    timer = setTimeout(() => {
      refresh();
      scheduleNextDay();
    }, nextDay - now.getTime() + 100);
  }
  function onVisible() {
    if (document.visibilityState === 'visible') {
      refresh();
      scheduleNextDay();
    }
  }
  watch(
    isLoggedIn,
    () => {
      applyStatus(null);
      open.value = false;
      refresh();
    },
    { immediate: true }
  );
  onMounted(() => {
    document.addEventListener('visibilitychange', onVisible);
    scheduleNextDay();
  });
  onScopeDispose(() => {
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisible);
  });
}

export function useDailyCheckInUi() {
  function openDialog() {
    today.value = new Date().toISOString().slice(0, 10);
    if (!promptAuthLogin()) return false;
    open.value = true;
    return true;
  }

  function closeDialog() {
    open.value = false;
  }

  return { open, status, checkedInToday, openDialog, closeDialog, refreshStatus, applyStatus };
}
